import {
  EventAction,
  EventCustomAttributes,
  UserActionTrackingActivity,
} from 'utils/analytics/amplitudeEventConstants';
import { activeCalendarFloorplanTablesSelector } from 'selectors/routes/calendar/calendarFloorplanSelector';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { debounce } from 'lodash';
import { tablesSelector } from 'selectors/tablesSelector';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import partyCountSelector from 'selectors/service/partyCountSelector';

const ACTIVE_TIMEOUT_MS = 10_000;

const TRACKED_PATHS: Map<RegExp, UserActionTrackingActivity> = new Map([
  [/^\/app\/\d{4}-\d{2}-\d{2}\/calendar\/(day|month)(\?.*)?$/i, 'timeline'],
  [/^\/app\/\d{4}-\d{2}-\d{2}\/service(\?.*)?$/i, 'service'],
]);

export const UserActionTracker: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation();
  const partyCount = useSelector(partyCountSelector);
  const activeTables = useSelector(tablesSelector);
  const activeFloorplan = useSelector(activeCalendarFloorplanTablesSelector);
  const trackedActivity = useMemo(() => {
    for (const [regex, domain] of TRACKED_PATHS.entries()) {
      if (location.pathname.match(regex)) {
        return domain;
      }
    }

    return null;
  }, [location.pathname]);
  const isTrackingActive = trackedActivity !== null;

  const userActiveStartTime = useRef(0);
  const numClicks = useRef(0);
  const numKeystrokes = useRef(0);
  const numScrolls = useRef(0);

  const totalActiveSeats = useMemo(() => {
    if (activeTables || activeFloorplan) {
      return (activeFloorplan || activeTables).reduce((sum, table) => sum + table.size, 0);
    }

    return 0;
  }, [activeFloorplan, activeTables]);

  const resetMetrics = useCallback(() => {
    userActiveStartTime.current = 0;
    numClicks.current = 0;
    numKeystrokes.current = 0;
    numScrolls.current = 0;
  }, []);

  // Send metrics to Amplitude and reset
  // Not debounced - generally shouldn't be called directly.
  const logMetrics = useCallback(() => {
    const totalPartyCount = partyCount.reservationPartyCount + partyCount.walkinPartyCount;
    if (
      userActiveStartTime.current === 0 ||
      !isTrackingActive ||
      (totalPartyCount === 0 && totalActiveSeats === 0)
    ) {
      return;
    }

    const timeActiveMs = Date.now() - userActiveStartTime.current;
    let amplitudeData: EventCustomAttributes = {
      activity: trackedActivity,
      clicks: numClicks.current,
      keystrokes: numKeystrokes.current,
      scrolls: numScrolls.current,
      timeActiveSec: Math.round(timeActiveMs / 1000),
    };

    if (totalPartyCount > 0) {
      amplitudeData = {
        ...amplitudeData,
        clicksPerParty: numClicks.current / totalPartyCount,
        keystrokesPerParty: numKeystrokes.current / totalPartyCount,
        scrollsPerParty: numScrolls.current / totalPartyCount,
      };
    }

    if (totalActiveSeats > 0) {
      amplitudeData = {
        ...amplitudeData,
        clicksPerSeat: numClicks.current / totalActiveSeats,
        keystrokesPerSeat: numKeystrokes.current / totalActiveSeats,
        scrollsPerSeat: numScrolls.current / totalActiveSeats,
      };
    }

    amplitudeSendEvent(EventAction.USER_ACTIVITY_TRACKING, amplitudeData);

    resetMetrics();
  }, [
    partyCount.reservationPartyCount,
    partyCount.walkinPartyCount,
    isTrackingActive,
    trackedActivity,
    totalActiveSeats,
    resetMetrics,
  ]);
  const logMetricsDebounced = useMemo(() => debounce(logMetrics, ACTIVE_TIMEOUT_MS), [logMetrics]);

  // The user has performed an action - reset the timer to log metrics
  const handleUserActive = useCallback(() => {
    if (userActiveStartTime.current === 0) {
      userActiveStartTime.current = Date.now();
    }

    logMetricsDebounced();
  }, [logMetricsDebounced]);

  const handleClick = useCallback(() => {
    numClicks.current += 1;
    handleUserActive();
  }, [handleUserActive]);

  const handleKeyPress = useCallback(() => {
    numKeystrokes.current += 1;
    handleUserActive();
  }, [handleUserActive]);

  // Scroll events need to be debounced because it triggers multiple times per second when scrolling
  const handleScroll = useCallback(() => {
    numScrolls.current += 1;
    handleUserActive();
  }, [handleUserActive]);
  const handleScrollDebounced = useMemo(() => debounce(handleScroll, 50), [handleScroll]);

  const setUpTracking = useCallback(() => {
    // The user arrived here while being active, so we start tracking immediately
    handleUserActive();

    document.addEventListener('mouseup', handleClick);
    document.addEventListener('dragend', handleClick);

    document.addEventListener('keypress', handleKeyPress);

    window.addEventListener('scroll', handleScrollDebounced, { passive: true, capture: true });
  }, [handleClick, handleKeyPress, handleScrollDebounced, handleUserActive]);

  const tearDownTracking = useCallback(() => {
    // Immediately log any metrics we've been tracking
    logMetricsDebounced.cancel();
    logMetrics();

    document.removeEventListener('mouseup', handleClick);
    document.removeEventListener('dragend', handleClick);

    document.removeEventListener('keypress', handleKeyPress);

    window.removeEventListener('scroll', handleScrollDebounced, { capture: true });
  }, [handleClick, handleKeyPress, handleScrollDebounced, logMetricsDebounced, logMetrics]);

  useEffect(() => {
    if (isTrackingActive) {
      setUpTracking();
      return tearDownTracking;
    } else {
      tearDownTracking();
      return undefined;
    }
  }, [isTrackingActive, setUpTracking, tearDownTracking]);

  return null;
};
